<template>
  <Popover :key="componentKey" v-model:open="open">
    <PopoverTrigger as-child>
      <Button
        role="combobox"
        variant="outline"
        :aria-expanded="open"
        :class="[comboBoxStyle({ disabled: props.disabled, isError: props.isError }), $attrs.class]"
        v-bind="$attrs"
        :disabled="props.disabled"
      >
        {{ selectedLabel || defaultText }}
        <ChevronDown class="absolute right-0 min-h-6 min-w-8 shrink-0 bg-white stroke-1" />
      </Button>
    </PopoverTrigger>
    <PopoverContent class="max-h-[--radix-popover-content-available-height] w-[--radix-popover-trigger-width] rounded-none border-light-bd-muted bg-white p-0 font-Spectral" @click.stop>
      <Command>
        <CommandInput :placeholder="defaultSearchText" />
        <CommandEmpty>{{ notFoundMessage }}</CommandEmpty>
        <CommandList>
          <CommandGroup :key="renderKey">
            <!-- shadcnコンポーネントの仕様上、valueに指定した値が検索対象となる(labelの値を検索対象にしている) -->
            <CommandItem
              v-for="option in options"
              :key="option.value"
              :class="cn(selectItemStyle({ disabled: option.disabled }), modelValue === option.value ? 'bg-beige1' : 'bg-white')"
              :data-testid="option.value"
              :value="option.label"
              @select="updateValue(option)"
            >
              <Check :class="cn('mr-2 h-4 w-4', modelValue === option.value ? 'opacity-100' : 'opacity-0')" />
              {{ option.label }}
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>

<script setup lang="ts">
import { Check, ChevronDown } from '@lucide/vue';
import { tv } from 'tailwind-variants';

import { Button } from '@/components/lib/shadcn-vue/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/lib/shadcn-vue/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/lib/shadcn-vue/popover';
import { cn } from '@/components/lib/shadcn-vue/utils';
import type { Lang } from '@/models/lang';
import type { Options, Option } from '@/models/option';

interface Props {
  options?: Options;
  modelValue: string | number;
  defaultValue?: string | number;
  disabled?: boolean;
  isError?: boolean;
  ariaLabel?: string;
  lang?: Lang;
}

const props = withDefaults(defineProps<Props>(), {
  options: () => [],
  modelValue: '',
  defaultValue: '',
  disabled: false,
  isError: false,
  ariaLabel: '',
  lang: 'jp',
});

//otonami/wabunkaでCSSを別定義する場合、props.langを判定条件として、CSSを切り替える
const comboBoxStyle = tv({
  base: 'relative box-border flex h-[48px] w-full items-center justify-between overflow-hidden rounded-none border border-light-bd-muted bg-white pl-[12px] pr-[32px] outline-none transition-all duration-100 ease-in-out',
  variants: {
    disabled: {
      true: 'pointer-events-none text-gray2 opacity-50',
    },
    isError: {
      true: 'border-[1px] border-solid border-red1 bg-pink2 text-red1',
    },
  },
});

const selectItemStyle = tv({
  base: 'h-[48px]',
  variants: {
    disabled: {
      true: 'pointer-events-none text-gray2',
    },
  },
});

interface Emit {
  (e: 'update:modelValue', value: string | number): void;
}

const emit = defineEmits<Emit>();

const options = ref(props.options);
const open = ref(false);
// modelValueの初期化はpropsから受け取った値で実行
const modelValue = ref(props.modelValue ?? props.defaultValue);

//ドロップダウンリストのdefaultValueが設定されていない場合の表示テキスト（langに応じて切り替える）
const defaultText = computed<string>(() => (props.lang === 'jp' ? '選択してください' : 'Please Select'));
const defaultSearchText = computed<string>(() => (props.lang === 'jp' ? '検索' : 'Search'));
const notFoundMessage = computed<string>(() => (props.lang === 'jp' ? '検索結果がありません。' : 'No results found.'));
const selectedLabel = computed(() => {
  const option = options.value.find((option) => option.value === modelValue.value);
  return option ? option.label : '';
});
const componentKey = ref(0);

watch(
  () => props.modelValue,
  (newValue) => {
    modelValue.value = newValue;
  },
);
const updateValue = async (option: Option) => {
  modelValue.value = option.value;
  emit('update:modelValue', option.value);
  open.value = false;
  componentKey.value++; // デフォルトだとPopoverが閉じないので、keyを変更して再描画させる
};

// コンポーネントのデータオプションにrenderKeyを追加
const renderKey = ref(0);
watch(
  () => props.options,
  (newOptions) => {
    options.value = newOptions;
  },
  { immediate: true },
);
</script>
